import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { usePolling } from '../hooks/usePolling'
import { partyApi, PartyList } from '../lib/api'
import PartyCard from '../components/PartyCard'

export default function DashboardPage() {
  const fetcher = useCallback(() => partyApi.list({ role: 'admin' }), [])
  const { data, loading, error, refetch } = usePolling<PartyList>({ fetcher, interval: 5000 })

  const handleEnd = async (id: string) => {
    try {
      await partyApi.end(id)
      refetch()
    } catch {
      // error is shown via polling refresh
    }
  }

  const parties = data?.parties ?? []
  const active = parties.filter((p) => p.status === 'active' || p.status === 'waiting')
  const totalParticipants = parties.reduce((sum, p) => sum + p.participant_count, 0)

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Party Dashboard</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Party Dashboard</h1>
        <Link
          to="/create"
          className="inline-flex items-center px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700"
        >
          Create Party
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          Failed to load parties. Retrying...
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total Parties" value={data?.total ?? 0} />
        <SummaryCard label="Waiting" value={parties.filter((p) => p.status === 'waiting').length} />
        <SummaryCard label="Active" value={parties.filter((p) => p.status === 'active').length} />
        <SummaryCard label="Participants" value={totalParticipants} />
      </div>

      {/* Party cards */}
      {active.length === 0 && !error ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No active parties</p>
          <Link
            to="/create"
            className="text-brand-600 hover:text-brand-700 font-medium"
          >
            Create your first party
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {active.map((party) => (
            <PartyCard key={party.id} party={party} onEnd={handleEnd} />
          ))}
        </div>
      )}
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    </div>
  )
}
